<template>
  <div class="ISTdateInput">
    <ISTDatePicker
      @onDayCreated="onDayCreate"
      @update:modelValue="inputChanged"
      @onErrorChange="onErrorChange"
      :label="label"
      :ref="refinput"
      :id="id"
      v-model="lvalue"
      :name="id"
      :dense="dense"
      :disable="disable"
      :error="error"
      :error-message="errorMessage"
      :min="min"
      :max="max"
      :multiple="multiple"
      :OnlyAllowEditOnFirstDate="OnlyAllowEditOnFirstDate"
    />
  </div>
</template>

<script setup lang="ts">
import "flatpickr/dist/flatpickr.css";
import ISTDatePicker from "./IstDatePicker.vue";
import { isSameDay, addYears, startOfYear, endOfYear } from "date-fns";
import store from "../store";
import { Holiday } from "../models/holiday";
import { Events } from "../models/events";
import { EventArray } from "../handlers/events-handler";
import i18n from "../i18n";
import { onMounted, ref, watch } from "vue";

defineOptions({ name: "DatePickerWithHolidays", components: { ISTDatePicker } });

const props = withDefaults(
  defineProps<{
    id?: string;
    modelValue?: Date | string | any[] | null;
    min?: Date;
    max?: Date;
    disable?: boolean;
    label?: string;
    multiple?: boolean;
    OnlyAllowEditOnFirstDate?: boolean;
    dense?: boolean | string;
    error?: boolean;
    errorMessage?: string;
  }>(),
  {
    id: "",
    modelValue: null,
    multiple: false,
    OnlyAllowEditOnFirstDate: false,
    dense: false,
    error: false
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", val: any): void;
  (e: "change", val: any): void;
  (e: "onErrorChange", hasError: boolean): void;
}>();

const refinput = ref<string>(props.id + "input");
const holidays = ref<Holiday[]>([]);
const events = ref<Events[]>([]);
const lvalue = ref<any>("");

watch(
  () => props.modelValue,
  () => {
    lvalue.value = props.modelValue as any;
  }
);

const inputChanged = (value: Date) => {
  lvalue.value = value;
  emit("update:modelValue", lvalue.value);
  emit("change", lvalue.value);
};

const onErrorChange = (hasError: boolean) => {
  emit("onErrorChange", hasError);
};

onMounted(async () => {
  lvalue.value = props.modelValue as any;
  const from = startOfYear(addYears(new Date(), -1));
  const to = endOfYear(addYears(new Date(), 1));
  setTimeout(() => {
    store.dispatch("events/fetchEventTypes").then(async () => {
      store
        .dispatch("events/fetchEvents", {
          from: from,
          to: to
        })
        .then((evts: Events[]) => (events.value = evts));

      for (let i = from.getFullYear(); i <= to.getFullYear() + 1; i++) {
        store.dispatch("holidays/fetchHolidays", i).then((list: Holiday[]) => {
          holidays.value = holidays.value.concat(list);
        });
      }
    });
  }, 100);
});

const onDayCreate = async (calendarElement: any) => {
  const isHoliday = holidays.value.find((h) => isSameDay(h.date, calendarElement.dateObj));
  let isWeekend = false;
  if (calendarElement.dateObj.getDay() === 6 || calendarElement.dateObj.getDay() === 0) {
    isWeekend = true;
  }

  const eventArray = new EventArray(events.value);
  const isStudentFreeDate = eventArray.isStudentFreeDate(calendarElement.dateObj);
  const isClosed = eventArray.isClosed(calendarElement.dateObj);

  if (isHoliday) {
    calendarElement.title = isHoliday.name;
    calendarElement.innerHTML += "<span class='event square'></span>";
    calendarElement.innerHTML += "<span class='grey-background'></span>";
  } else if (isWeekend) {
    calendarElement.innerHTML += "<span class='event circle'></span>";
    calendarElement.innerHTML += "<span class='grey-background'></span>";
  } else if (isClosed) {
    calendarElement.title = i18n.global.t("closed");
    calendarElement.innerHTML += "<span class='event circle'></span>";
    calendarElement.innerHTML += "<span class='grey-background'></span>";
  } else if (isStudentFreeDate) {
    calendarElement.title = i18n.global.t("studentFreeDay");
    calendarElement.innerHTML += "<span class='event triangletopleft'></span>";
    calendarElement.innerHTML += "<span class='grey-background'></span>";
  }
};
</script>

<style scoped></style>
